/**
 * Save / unsave control.
 *
 * An inline SVG, not a `♥` text glyph: the glyph's metrics and weight vary
 * wildly between Android system fonts, and its outline form renders thin and
 * washed out — which is exactly how the first version looked on a phone.
 *
 * Two variants, because the same control sits in two very different places:
 *  - 'card'  no circle, no border. On a white product card a bordered white
 *            circle reads as something pasted on top and shouts louder than the
 *            model number. Here it is just an icon with a 44px invisible hit
 *            area (the accessible minimum) around a 22px glyph.
 *  - 'bar'   the product-detail action bar, where it matches the neighbouring
 *            copy button exactly so the three controls read as one row.
 *
 * `stopPropagation` matters on a card: the whole card is a tap target, so
 * without it saving would also open the product.
 */
import { useEffect, useRef, useState } from 'react';

function HeartIcon({ filled, size = 22 }: { filled: boolean; size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20.3 4.6 13a4.8 4.8 0 0 1 0-6.8 4.8 4.8 0 0 1 6.8 0l.6.6.6-.6a4.8 4.8 0 0 1 6.8 0 4.8 4.8 0 0 1 0 6.8Z" />
    </svg>
  );
}

/** Exported so the header count pill uses the identical glyph. */
export { HeartIcon };

export function HeartButton({
  saved,
  onToggle,
  variant = 'card',
  label,
}: {
  saved: boolean;
  onToggle: () => void;
  variant?: 'card' | 'bar';
  /** Optional visible text (unused on cards, where space is tight). */
  label?: string;
}): JSX.Element {
  // A brief pop when it becomes saved — the only feedback a dealer gets that the
  // tap registered, since the write itself is debounced and invisible.
  const [pop, setPop] = useState(false);
  const was = useRef(saved);
  useEffect(() => {
    if (saved && !was.current) {
      setPop(true);
      const t = setTimeout(() => setPop(false), 220);
      return () => clearTimeout(t);
    }
    was.current = saved;
    return undefined;
  }, [saved]);
  was.current = saved;

  const chrome =
    variant === 'bar'
      ? `h-[46px] w-[52px] rounded-full bg-white shadow-mini ${saved ? 'text-hot' : 'text-slate-400'}`
      : // -m-2.5 p-2.5 gives a 44px touch target without the icon looking huge.
        `-m-2.5 p-2.5 ${saved ? 'text-hot' : 'text-slate-400'}`;

  return (
    <button
      type="button"
      aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
      aria-pressed={saved}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`flex shrink-0 items-center justify-center gap-2 transition-transform duration-150 active:scale-90 ${chrome} ${
        label ? 'px-4 text-[14px] font-bold' : ''
      }`}
      style={pop ? { transform: 'scale(1.22)' } : undefined}
    >
      <HeartIcon filled={saved} size={variant === 'bar' ? 21 : 22} />
      {label ? <span>{label}</span> : null}
    </button>
  );
}
